import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { STAGE_LABELS, usd, fmtDate, fmtTime } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Phone, Mail, MapPin, ArrowLeft, Home, MessageSquare, FileText, HardHat, Receipt, FolderOpen, Pencil, Plus, X, Save, ArrowRight, Trash2, Sliders, Check } from "lucide-react";
import FilesPanel from "@/components/FilesPanel";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import MeasurementHistory from "@/components/MeasurementHistory";
import { EmailDocButton } from "@/components/EmailDocButton";
import { CustomizeProposalModal } from "@/components/CustomizeProposalModal";
import { ROOF_TYPES, tLabel, proposalTypeLabel } from "@/lib/roofTypes";
import { toast } from "sonner";

const PIPELINE = ["new", "qualified", "appointment_set", "measured", "estimated", "proposal_sent", "signed", "in_production", "complete", "warranty", "referral"];

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="mb-card p-6 w-full max-w-md animate-scale-in my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl font-bold uppercase text-navy">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [editC, setEditC] = useState(null);   // contact edit form
  const [editP, setEditP] = useState(null);   // property edit/add form
  const [editL, setEditL] = useState(null);   // lead edit form
  const [newProp, setNewProp] = useState(null);  // new proposal from estimate
  const [logComm, setLogComm] = useState(null);  // log communication
  const [newInv, setNewInv] = useState(null);     // new invoice
  const [newJob, setNewJob] = useState(null);     // manual job
  const [customizing, setCustomizing] = useState(null);  // customize proposal
  const [templates, setTemplates] = useState([]);

  const load = () => api.get(`/contacts/${id}`).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/scope-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  if (!c) return <div className="text-navy font-display text-xl">Loading…</div>;

  // ---- Contact ----
  const saveContact = async () => {
    if (!editC.name?.trim()) return toast.error("Name is required");
    try { await api.put(`/contacts/${id}`, editC); toast.success("Customer updated"); setEditC(null); load(); }
    catch { toast.error("Update failed"); }
  };

  const deleteContact = async () => {
    if (!window.confirm(`Delete customer "${c.name}"? They'll be removed from your lists (records are kept and reversible).`)) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Customer deleted"); nav("/crm"); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Property ----
  const saveProperty = async () => {
    const body = { address: editP.address, pitch: editP.pitch, current_material: editP.current_material,
      stories: Number(editP.stories) || 1, roof_age_years: Number(editP.roof_age_years) || 0,
      squares: Number(editP.squares) || 0, sqft_roof: Math.round((Number(editP.squares) || 0) * 100),
      hail_history: !!editP.hail_history };
    try {
      if (editP.id) await api.put(`/properties/${editP.id}`, body);
      else await api.post("/properties", { ...body, contact_id: id });
      toast.success("Property saved"); setEditP(null); load();
    } catch { toast.error("Save failed"); }
  };

  // ---- Lead ----
  const saveLead = async () => {
    try {
      await api.put(`/leads/${editL.id}`, {
        status: editL.status, lead_source: editL.lead_source, notes: editL.notes,
        value_cents: Math.round((Number(editL.valueDollars) || 0) * 100),
      });
      toast.success("Opportunity updated"); setEditL(null); load();
    } catch { toast.error("Update failed"); }
  };

  // ---- Push estimate -> proposal ----
  const pushToProposal = async (estimateId, tier) => {
    try {
      const { data } = await api.post("/proposals", { estimate_id: estimateId, selected_tier: tier });
      if (data?.existing) toast.info("A proposal already exists for this estimate — opening it below.");
      else toast.success(`${tier} pushed to proposal`);
      setNewProp(null); load();
    } catch { toast.error("Could not create proposal"); }
  };

  const delEstimate = async (eid) => {
    if (!window.confirm("Delete this estimate? This cannot be undone.")) return;
    try { await api.delete(`/estimates/${eid}`); toast.success("Estimate deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const delProposal = async (pid) => {
    if (!window.confirm("Delete this proposal? This cannot be undone.")) return;
    try { await api.delete(`/proposals/${pid}`); toast.success("Proposal deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Communications ----
  const saveComm = async () => {
    if (!logComm.summary?.trim()) return toast.error("Add a short summary");
    try { await api.post("/communications", { contact_id: id, ...logComm }); toast.success("Communication logged"); setLogComm(null); load(); }
    catch { toast.error("Could not log"); }
  };

  // ---- Invoice ----
  const saveInvoice = async () => {
    const body = { contact_id: id, type: newInv.type, notes: newInv.notes };
    if (newInv.due_date) body.due_date = new Date(newInv.due_date).toISOString();
    if (newInv.itemized) {
      body.line_items = newInv.items.filter((i) => i.description.trim()).map((i) => ({ description: i.description, qty: Number(i.qty) || 1, unit_price_cents: Math.round(Number(i.price || 0) * 100) }));
      if (!body.line_items.length) return toast.error("Add at least one line item");
    } else {
      body.amount_cents = Math.round(Number(newInv.amount || 0) * 100);
      if (body.amount_cents <= 0) return toast.error("Enter an amount");
    }
    try { await api.post("/invoices", body); toast.success("Invoice created"); setNewInv(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create invoice"); }
  };

  // ---- Manual job ----
  const saveJob = async () => {
    const amt = Math.round(Number(newJob.amount || 0) * 100);
    if (amt <= 0) return toast.error("Enter a contract amount");
    try { await api.post("/jobs", { contact_id: id, address: newJob.address, contract_amount_cents: amt }); toast.success("Job created"); setNewJob(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create job"); }
  };

  return (
    <div>
      <button onClick={() => nav("/crm")} className="flex items-center gap-1 text-sm text-slate-500 mb-3 hover:text-navy" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Back to pipeline</button>
      <div className="mb-card p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold uppercase text-navy">{c.name}</h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
              {c.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {c.phone}</span>}
              {c.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {c.email}</span>}
              {c.address && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {c.address}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setEditC({ name: c.name, phone: c.phone, email: c.email, address: c.address, lead_source: c.lead_source })} testid="edit-contact-btn">
                <Pencil className="w-4 h-4" /> Edit Customer
              </Btn>
              <button onClick={deleteContact} data-testid="delete-contact-btn" title="Delete customer" className="flex items-center gap-1 text-sm font-semibold text-brandred border-2 border-slate-200 hover:border-brandred rounded-xl px-3 py-1.5">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
            <div className="flex gap-2">
              <Pill tone="navy">{c.lead_source}</Pill>
              {c.leads?.[0] && <Pill tone="red">{STAGE_LABELS[c.leads[0].status]}</Pill>}
            </div>
          </div>
        </div>
      </div>

      {(() => {
        const hasLead = (c.leads?.length || 0) > 0;
        const hasEstimate = (c.estimates?.length || 0) > 0;
        const hasProposal = (c.proposals?.length || 0) > 0;
        const steps = [
          { key: "opp", label: "Opportunity", done: hasLead },
          { key: "est", label: "Estimate", done: hasEstimate },
          { key: "prop", label: "Proposal", done: hasProposal },
        ];
        const currentIdx = steps.findIndex((s) => !s.done);
        return (
          <div className="mb-card p-5 mb-6" data-testid="order-flow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold uppercase text-navy">Order Flow</h3>
              <span className="text-xs text-slate-400">Follow the steps — one estimate, one proposal</span>
            </div>
            <div className="flex items-center gap-1">
              {steps.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div data-testid={`flow-step-${s.key}`} className="flex items-center gap-2">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${s.done ? "bg-successg text-white" : i === currentIdx ? "bg-navy text-white" : "bg-slate-200 text-slate-500"}`}>
                      {s.done ? <Check className="w-4 h-4" /> : i + 1}
                    </span>
                    <span className={`text-sm font-semibold ${s.done || i === currentIdx ? "text-navy" : "text-slate-400"}`}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${s.done ? "bg-successg" : "bg-slate-200"}`} />}
                </React.Fragment>
              ))}
            </div>
            {currentIdx === 0 && <p className="text-sm text-slate-500 mt-4">Add this customer to the pipeline from the CRM board to open an opportunity.</p>}
            {currentIdx === 1 && <Btn onClick={() => nav(`/estimating?contact=${id}`)} testid="flow-build-estimate" className="mt-4"><HardHat className="w-4 h-4" /> Build Estimate</Btn>}
            {currentIdx === 2 && <Btn onClick={() => setNewProp({ estimate_id: c.estimates?.[0]?.id || "", tier: "SHINGLE" })} testid="flow-create-proposal" className="mt-4"><FileText className="w-4 h-4" /> Create Proposal from Estimate</Btn>}
            {currentIdx === -1 && (
              <div className="mt-4 flex items-center gap-2 text-successg text-sm font-bold" data-testid="flow-complete">
                <Check className="w-4 h-4" /> Proposal ready —
                <a href={`/proposal/${c.proposals[0].id}`} target="_blank" rel="noreferrer" className="underline text-navy" data-testid="flow-open-proposal">open it</a>
              </div>
            )}
          </div>
        );
      })()}


      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 rounded-xl p-1 flex-wrap h-auto">
          {[["overview", "Overview", Home], ["communications", "Communications", MessageSquare], ["proposals", "Proposals", FileText], ["jobs", "Jobs", HardHat], ["payments", "Payments", Receipt], ["files", "Files", FolderOpen]].map(([v, l, I]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-navy data-[state=active]:text-white rounded-lg text-xs sm:text-sm"><I className="w-3.5 h-3.5 mr-1" /> {l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="mb-card p-5">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-display font-bold uppercase text-navy">Properties</h3>
                <button data-testid="add-property-btn" onClick={() => setEditP({ address: c.address || "", pitch: "6/12", stories: 1, current_material: "Asphalt", roof_age_years: 15, squares: 0, hail_history: false })}
                  className="text-xs font-semibold text-navy flex items-center gap-1 hover:text-brandred"><Plus className="w-3.5 h-3.5" /> Add</button>
              </div>
              {c.properties?.length ? c.properties.map((p) => (
                <div key={p.id} className="border-b border-slate-100 py-2 last:border-0 text-sm group" data-testid={`property-${p.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-navy">{p.address || "No address"}</div>
                      <div className="text-slate-500 text-xs font-num">{p.squares} sq · {p.pitch} · {p.stories}-story · {p.current_material} · {p.roof_age_years}yr {p.hail_history && "· ⚡ hail"}</div>
                    </div>
                    <button data-testid={`edit-property-${p.id}`} onClick={() => setEditP({ ...p })} className="text-slate-400 hover:text-navy shrink-0 ml-2"><Pencil className="w-4 h-4" /></button>
                  </div>
                  <MeasurementHistory propertyId={p.id} contactId={id} onReused={load} />
                </div>
              )) : <p className="text-sm text-slate-400">No properties — add one so estimates use the right address & size.</p>}
            </div>
            <div className="mb-card p-5">
              <h3 className="font-display font-bold uppercase text-navy mb-2">Opportunities</h3>
              {c.leads?.map((l) => (
                <div key={l.id} className="py-2 border-b border-slate-100 last:border-0 text-sm" data-testid={`lead-${l.id}`}>
                  <div className="flex justify-between items-center">
                    <Pill tone="navy">{STAGE_LABELS[l.status]}</Pill>
                    <div className="flex items-center gap-2">
                      {l.value_cents ? <span className="font-num text-successg font-bold">{usd(l.value_cents)}</span> : null}
                      <button data-testid={`edit-lead-${l.id}`} onClick={() => setEditL({ id: l.id, status: l.status, lead_source: l.lead_source, notes: l.notes || "", valueDollars: (l.value_cents || 0) / 100 })} className="text-slate-400 hover:text-navy"><Pencil className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {l.notes && <div className="text-xs text-slate-500 mt-1">{l.notes}</div>}
                  <div className="text-xs text-slate-400 mt-0.5">{l.lead_source} · {fmtDate(l.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="communications" className="mt-4">
          <div className="mb-card p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-bold uppercase text-navy">Communications</h3>
              <button data-testid="log-comm-btn" onClick={() => setLogComm({ channel: "call", direction: "outbound", summary: "" })} className="text-xs font-semibold text-navy flex items-center gap-1 hover:text-brandred"><Plus className="w-3.5 h-3.5" /> Log</button>
            </div>
            <div className="space-y-3">
            {c.communications?.length ? c.communications.map((m) => (
              <div key={m.id} className="flex gap-3 border-b border-slate-100 pb-3 last:border-0">
                <Pill tone={m.direction === "inbound" ? "blue" : "navy"}>{m.channel}</Pill>
                <div className="flex-1"><div className="text-sm text-slate-700">{m.summary}</div><div className="text-xs text-slate-400">{fmtTime(m.created_at)} · {m.direction}</div></div>
              </div>
            )) : <p className="text-sm text-slate-400">No communications logged</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="proposals" className="mt-4 space-y-4">
          <div className="mb-card p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-bold uppercase text-navy">Estimates</h3>
              <button data-testid="new-proposal-btn" onClick={() => setNewProp({ estimate_id: c.estimates?.[0]?.id || "", tier: "SHINGLE" })} className="text-xs font-semibold text-navy flex items-center gap-1 hover:text-brandred"><Plus className="w-3.5 h-3.5" /> New Proposal</button>
            </div>
            {c.estimates?.length ? c.estimates.map((e) => (
              <div key={e.id} data-testid={`estimate-${e.id}`} className="border-b border-slate-100 py-3 last:border-0">
                <div className="flex items-center gap-2 mb-2 text-sm">
                  <span className="font-num font-bold text-navy">{e.squares} sq</span>
                  {e.auto_estimated && <Pill tone="amber">rough</Pill>}
                  <span className="text-xs text-slate-400">{fmtDate(e.created_at)}</span>
                  <button onClick={() => delEstimate(e.id)} data-testid={`delete-estimate-${e.id}`} className="ml-auto text-slate-300 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ROOF_TYPES.map((t) => (
                    <button key={t} data-testid={`push-${e.id}-${t}`} onClick={() => pushToProposal(e.id, t)}
                      className="text-xs font-semibold border-2 border-slate-200 hover:border-navy rounded-lg px-2.5 py-1.5 text-navy flex items-center gap-1">
                      {tLabel(t)} · {usd(e.variants?.[t]?.total_cents || 0)} <ArrowRight className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              </div>
            )) : <p className="text-sm text-slate-400">No estimates yet — build one in Estimating.</p>}
          </div>
          <div className="mb-card p-5 space-y-2">
            {c.proposals?.length ? c.proposals.map((p) => (
              <div key={p.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                <div><span className="font-num font-bold text-navy">{usd(p.amount_cents)}</span> <span className="text-xs text-slate-400 ml-2">{proposalTypeLabel(p)}</span></div>
                <div className="flex items-center gap-2"><Pill tone={p.status === "signed" ? "green" : "amber"}>{p.status?.replace("_", " ")}</Pill>
                  <a href={`/proposal/${p.id}`} target="_blank" rel="noreferrer" className="text-xs text-navy font-semibold underline">View</a>
                  <button onClick={() => setCustomizing(p)} data-testid={`customize-proposal-${p.id}`} className="text-slate-400 hover:text-navy" title="Edit proposal (tiers, scope, paperwork)"><Sliders className="w-4 h-4" /></button>
                  <EmailDocButton endpoint={`/proposals/${p.id}/email`} testid={`prop-email-${p.id}`} className="text-xs text-brandred font-semibold underline inline-flex items-center gap-1" />
                  <button onClick={() => delProposal(p.id)} data-testid={`delete-proposal-${p.id}`} className="text-slate-300 hover:text-brandred" title="Delete proposal"><Trash2 className="w-4 h-4" /></button></div>
              </div>
            )) : <p className="text-sm text-slate-400">No proposals</p>}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="mt-4">
          <div className="mb-card p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-bold uppercase text-navy">Jobs</h3>
              <button data-testid="new-job-btn" onClick={() => setNewJob({ address: c.address || "", amount: "" })} className="text-xs font-semibold text-navy flex items-center gap-1 hover:text-brandred"><Plus className="w-3.5 h-3.5" /> New Job</button>
            </div>
            <div className="space-y-2">
            {c.jobs?.length ? c.jobs.map((j) => (
              <div key={j.id} onClick={() => nav(`/production/job/${j.id}`)} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 rounded px-2">
                <div><span className="font-semibold text-navy">{j.address}</span></div>
                <div className="flex items-center gap-2"><span className="font-num text-successg">{usd(j.contract_amount_cents)}</span><Pill tone="navy">{j.status?.replace("_", " ")}</Pill></div>
              </div>
            )) : <p className="text-sm text-slate-400">No jobs</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="mb-card p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-bold uppercase text-navy">Invoices</h3>
              <button data-testid="new-invoice-btn" onClick={() => setNewInv({ type: "final", amount: "", notes: "", due_date: "", itemized: false, items: [{ description: "", qty: 1, price: "" }] })} className="text-xs font-semibold text-navy flex items-center gap-1 hover:text-brandred"><Plus className="w-3.5 h-3.5" /> New Invoice</button>
            </div>
            <div className="space-y-2">
            {c.invoices?.length ? c.invoices.map((v) => (
              <div key={v.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                <div><span className="font-num font-bold text-navy">{usd(v.amount_cents)}</span> <span className="text-xs text-slate-400 ml-2">{v.type}</span></div>
                <Pill tone={v.status === "paid" ? "green" : v.status === "overdue" ? "danger" : "amber"}>{v.status}</Pill>
              </div>
            )) : <p className="text-sm text-slate-400">No invoices</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <FilesPanel contactId={id} />
        </TabsContent>
      </Tabs>

      {/* Edit Customer */}
      {editC && (
        <Modal title="Edit Customer" onClose={() => setEditC(null)}>
          <div className="space-y-3">
            {[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["lead_source", "Lead Source"]].map(([k, l]) => (
              <Field key={k} label={l}>
                <input data-testid={`contact-${k}`} value={editC[k] || ""} onChange={(e) => setEditC({ ...editC, [k]: e.target.value })} className={inputCls} />
              </Field>
            ))}
            <Field label="Address">
              <AddressAutocomplete testid="contact-address" value={editC.address || ""} onChange={(v) => setEditC({ ...editC, address: v })} className={inputCls} />
            </Field>
          </div>
          <Btn onClick={saveContact} className="w-full mt-4" testid="save-contact"><Save className="w-4 h-4" /> Save Customer</Btn>
        </Modal>
      )}

      {/* Edit / Add Property */}
      {editP && (
        <Modal title={editP.id ? "Edit Property" : "Add Property"} onClose={() => setEditP(null)}>
          <div className="space-y-3">
            <Field label="Address"><AddressAutocomplete testid="prop-address" value={editP.address || ""} onChange={(v) => setEditP({ ...editP, address: v })} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Roof Size (squares)"><input data-testid="prop-squares" type="number" value={editP.squares ?? ""} onChange={(e) => setEditP({ ...editP, squares: e.target.value })} className={`${inputCls} font-num`} /></Field>
              <Field label="Pitch"><input data-testid="prop-pitch" value={editP.pitch || ""} onChange={(e) => setEditP({ ...editP, pitch: e.target.value })} className={inputCls} placeholder="6/12" /></Field>
              <Field label="Stories"><input data-testid="prop-stories" type="number" value={editP.stories ?? ""} onChange={(e) => setEditP({ ...editP, stories: e.target.value })} className={`${inputCls} font-num`} /></Field>
              <Field label="Roof Age (yrs)"><input data-testid="prop-age" type="number" value={editP.roof_age_years ?? ""} onChange={(e) => setEditP({ ...editP, roof_age_years: e.target.value })} className={`${inputCls} font-num`} /></Field>
            </div>
            <Field label="Current Material"><input data-testid="prop-material" value={editP.current_material || ""} onChange={(e) => setEditP({ ...editP, current_material: e.target.value })} className={inputCls} /></Field>
            <label className="flex items-center gap-2 text-sm text-navy font-semibold"><input data-testid="prop-hail" type="checkbox" checked={!!editP.hail_history} onChange={(e) => setEditP({ ...editP, hail_history: e.target.checked })} /> Hail history</label>
          </div>
          <Btn onClick={saveProperty} className="w-full mt-4" testid="save-property"><Save className="w-4 h-4" /> Save Property</Btn>
        </Modal>
      )}

      {/* Edit Opportunity (lead) */}
      {editL && (
        <Modal title="Edit Opportunity" onClose={() => setEditL(null)}>
          <div className="space-y-3">
            <Field label="Stage">
              <select data-testid="lead-status" value={editL.status} onChange={(e) => setEditL({ ...editL, status: e.target.value })} className={inputCls}>
                {PIPELINE.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
            </Field>
            <Field label="Deal Value ($)"><input data-testid="lead-value" type="number" value={editL.valueDollars} onChange={(e) => setEditL({ ...editL, valueDollars: e.target.value })} className={`${inputCls} font-num`} /></Field>
            <Field label="Lead Source"><input data-testid="lead-source" value={editL.lead_source || ""} onChange={(e) => setEditL({ ...editL, lead_source: e.target.value })} className={inputCls} /></Field>
            <Field label="Notes"><textarea data-testid="lead-notes" rows={3} value={editL.notes} onChange={(e) => setEditL({ ...editL, notes: e.target.value })} className={inputCls} /></Field>
          </div>
          <Btn onClick={saveLead} className="w-full mt-4" testid="save-lead"><Save className="w-4 h-4" /> Save Opportunity</Btn>
        </Modal>
      )}

      {/* New Proposal from estimate */}
      {newProp && (
        <Modal title="New Proposal" onClose={() => setNewProp(null)}>
          {c.estimates?.length ? (
            <div className="space-y-3">
              <Field label="Estimate">
                <select data-testid="np-estimate" value={newProp.estimate_id} onChange={(e) => setNewProp({ ...newProp, estimate_id: e.target.value })} className={inputCls}>
                  {c.estimates.map((e) => <option key={e.id} value={e.id}>{e.squares} sq · {fmtDate(e.created_at)}{e.auto_estimated ? " (rough)" : ""}</option>)}
                </select>
              </Field>
              <Field label="Roof Type">
                <select data-testid="np-tier" value={newProp.tier} onChange={(e) => setNewProp({ ...newProp, tier: e.target.value })} className={inputCls}>
                  {ROOF_TYPES.map((t) => <option key={t} value={t}>{tLabel(t)}</option>)}
                </select>
              </Field>
              <Btn onClick={() => pushToProposal(newProp.estimate_id, newProp.tier)} className="w-full mt-1" testid="np-create"><FileText className="w-4 h-4" /> Create Proposal</Btn>
            </div>
          ) : <p className="text-sm text-slate-400">No estimates yet. Build one in Estimating first.</p>}
        </Modal>
      )}

      {/* Log Communication */}
      {logComm && (
        <Modal title="Log Communication" onClose={() => setLogComm(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Channel">
                <select data-testid="comm-channel" value={logComm.channel} onChange={(e) => setLogComm({ ...logComm, channel: e.target.value })} className={inputCls}>
                  {["call", "text", "email", "note"].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </Field>
              <Field label="Direction">
                <select data-testid="comm-direction" value={logComm.direction} onChange={(e) => setLogComm({ ...logComm, direction: e.target.value })} className={inputCls}>
                  {["outbound", "inbound"].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Summary">
              <textarea data-testid="comm-summary" rows={3} value={logComm.summary} onChange={(e) => setLogComm({ ...logComm, summary: e.target.value })} className={inputCls} placeholder="What was discussed?" />
            </Field>
          </div>
          <Btn onClick={saveComm} className="w-full mt-4" testid="save-comm"><Save className="w-4 h-4" /> Log</Btn>
        </Modal>
      )}

      {/* New Invoice */}
      {newInv && (
        <Modal title="New Invoice" onClose={() => setNewInv(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select data-testid="inv-type" value={newInv.type} onChange={(e) => setNewInv({ ...newInv, type: e.target.value })} className={inputCls}>
                  {["deposit", "progress", "final", "full"].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </Field>
              <Field label="Due Date">
                <input data-testid="inv-due" type="date" value={newInv.due_date} onChange={(e) => setNewInv({ ...newInv, due_date: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-navy font-semibold"><input data-testid="inv-itemized" type="checkbox" checked={newInv.itemized} onChange={(e) => setNewInv({ ...newInv, itemized: e.target.checked })} /> Itemized (line items)</label>
            {newInv.itemized ? (
              <div className="space-y-2">
                {newInv.items.map((it, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`inv-item-${i}`}>
                    <input value={it.description} onChange={(e) => setNewInv({ ...newInv, items: newInv.items.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x) })} placeholder="Description" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-navy" />
                    <input type="number" value={it.qty} onChange={(e) => setNewInv({ ...newInv, items: newInv.items.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x) })} placeholder="Qty" className="w-16 border-2 border-slate-200 rounded-lg px-2 py-1 text-sm font-num outline-none focus:border-navy" />
                    <input type="number" value={it.price} onChange={(e) => setNewInv({ ...newInv, items: newInv.items.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x) })} placeholder="$ each" className="w-24 border-2 border-slate-200 rounded-lg px-2 py-1 text-sm font-num outline-none focus:border-navy" />
                    <button onClick={() => setNewInv({ ...newInv, items: newInv.items.filter((_, idx) => idx !== i) })} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button data-testid="inv-add-item" onClick={() => setNewInv({ ...newInv, items: [...newInv.items, { description: "", qty: 1, price: "" }] })} className="text-xs text-navy font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add line item</button>
              </div>
            ) : (
              <Field label="Amount ($)"><input data-testid="inv-amount" type="number" value={newInv.amount} onChange={(e) => setNewInv({ ...newInv, amount: e.target.value })} className={`${inputCls} font-num`} /></Field>
            )}
            <Field label="Notes"><input data-testid="inv-notes" value={newInv.notes} onChange={(e) => setNewInv({ ...newInv, notes: e.target.value })} className={inputCls} /></Field>
          </div>
          <Btn onClick={saveInvoice} className="w-full mt-4" testid="save-invoice"><Save className="w-4 h-4" /> Create Invoice</Btn>
        </Modal>
      )}

      {/* New Job */}
      {newJob && (
        <Modal title="New Job" onClose={() => setNewJob(null)}>
          <div className="space-y-3">
            <Field label="Job Address"><AddressAutocomplete testid="job-address" value={newJob.address || ""} onChange={(v) => setNewJob({ ...newJob, address: v })} className={inputCls} /></Field>
            <Field label="Contract Amount ($)"><input data-testid="job-amount" type="number" value={newJob.amount} onChange={(e) => setNewJob({ ...newJob, amount: e.target.value })} className={`${inputCls} font-num`} /></Field>
          </div>
          <Btn onClick={saveJob} className="w-full mt-4" testid="save-job"><Save className="w-4 h-4" /> Create Job</Btn>
        </Modal>
      )}

      {customizing && <CustomizeProposalModal proposal={customizing} templates={templates} onClose={() => setCustomizing(null)} onSaved={() => { setCustomizing(null); load(); }} />}
    </div>
  );
}
